import { useState, forwardRef } from "react";
import axios from "axios";
import { Loader2, Send, CheckCircle2, ChevronDown, ShieldCheck, MailCheck, Lock } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Reveal, Overline } from "@/components/site/Reveal";
import { API } from "@/lib/site";

const grades = ["Class 1", "Class 2", "Class 3", "Class 4", "Class 5", "Class 6", "Class 7", "Class 8"];
const interests = ["Specific Subjects", "All Subjects", "Languages", "Vedic Maths / Abacus", "Spoken English", "Computer", "Olympiad Prep"];

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isEmailValid = (e) => emailRe.test((e || "").trim());
const phoneDigits = (p) => (p || "").replace(/\D/g, "").slice(0, 10);
const isPhoneValid = (p) => /^[6-9]\d{9}$/.test(phoneDigits(p));

export const DemoForm = forwardRef((props, ref) => {
  const [form, setForm] = useState({ parent_name: "", email: "", phone: "", grade: "", plan_interest: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  // OTP state
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [emailVerified, setEmailVerified] = useState(false);
  const [verificationToken, setVerificationToken] = useState("");
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onEmailChange = (v) => {
    setForm((f) => ({ ...f, email: v }));
    if (emailVerified || otpSent) {
      setEmailVerified(false);
      setOtpSent(false);
      setOtp("");
      setVerificationToken("");
    }
  };

  const sendOtp = async () => {
    if (!isEmailValid(form.email)) { toast.error("Enter a valid email address first."); return; }
    setSendingOtp(true);
    try {
      await axios.post(`${API}/otp/send`, { email: form.email.trim() });
      setOtpSent(true);
      toast.success("Verification code sent. Check your inbox.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send the code. Please try again.");
    } finally {
      setSendingOtp(false);
    }
  };

  const verifyOtp = async () => {
    if (otp.trim().length !== 6) { toast.error("Enter the 6-digit code."); return; }
    setVerifyingOtp(true);
    try {
      const { data } = await axios.post(`${API}/otp/verify`, { email: form.email.trim(), code: otp.trim() });
      setVerificationToken(data.verification_token);
      setEmailVerified(true);
      toast.success("Email verified!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Incorrect code. Please try again.");
    } finally {
      setVerifyingOtp(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.parent_name || !form.email || !form.phone || !form.grade || !form.plan_interest) {
      toast.error("Please fill in all required fields."); return;
    }
    if (!isEmailValid(form.email)) { toast.error("Please enter a valid email address."); return; }
    if (!isPhoneValid(form.phone)) { toast.error("Please enter a valid 10-digit Indian mobile number (starting 6-9)."); return; }
    if (!emailVerified) { toast.error("Please verify your email before submitting."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, phone: phoneDigits(form.phone), verification_token: verificationToken });
      setDone(true);
      toast.success("Demo request received! We'll call you shortly.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const emailValid = isEmailValid(form.email);
  const phoneValid = form.phone === "" || isPhoneValid(form.phone);

  return (
    <section id="demo" ref={ref} className="py-24 md:py-32 max-w-7xl mx-auto px-5 md:px-8">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <Reveal>
          <Overline>Book a free demo</Overline>
          <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight leading-[1.05]">
            See the difference in one class, <span className="text-secondary">on us.</span>
          </h2>
          <p className="mt-5 text-muted-foreground text-base md:text-lg max-w-md">
            Tell us a little about your child. Our academic counsellor will call to schedule a free demo and craft a plan around their grade, board and goals.
          </p>
          <ul className="mt-8 space-y-3">
            {["No payment needed to start", "A plan tailored to your child", "Meet the tutor before you commit"].map((t) => (
              <li key={t} className="flex items-center gap-3"><CheckCircle2 className="text-secondary" size={20} /><span className="text-foreground/80">{t}</span></li>
            ))}
          </ul>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="bg-white rounded-3xl border border-black/5 shadow-[0_8px_30px_rgb(0,0,0,0.06)] p-7 md:p-9">
            {done ? (
              <div className="text-center py-12" data-testid="demo-success">
                <CheckCircle2 className="text-secondary mx-auto" size={56} />
                <h3 className="font-heading font-bold text-2xl mt-4">You're all set!</h3>
                <p className="text-muted-foreground mt-2">A confirmation is on its way to your inbox. We'll be in touch very soon.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="demo-form">
                <Input data-testid="demo-name" placeholder="Parent / Student name *" value={form.parent_name} onChange={(e) => set("parent_name", e.target.value)} className="rounded-xl bg-muted border-transparent h-12 focus-visible:ring-2 focus-visible:ring-primary" />

                {/* Email + verification */}
                <div>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Input data-testid="demo-email" type="email" placeholder="Email *" value={form.email} onChange={(e) => onEmailChange(e.target.value)} disabled={emailVerified}
                        className={`rounded-xl bg-muted border-transparent h-12 pr-10 focus-visible:ring-2 focus-visible:ring-primary ${form.email && !emailValid ? "ring-2 ring-destructive" : ""}`} />
                      {emailVerified && <MailCheck size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary" />}
                    </div>
                    {!emailVerified && (
                      <button type="button" data-testid="demo-send-otp" onClick={sendOtp} disabled={!emailValid || sendingOtp}
                        className="shrink-0 rounded-xl bg-primary text-white px-4 h-12 text-sm font-semibold hover:-translate-y-0.5 transition-transform duration-200 disabled:opacity-50 disabled:translate-y-0 flex items-center gap-2">
                        {sendingOtp ? <Loader2 className="animate-spin" size={16} /> : null}{otpSent ? "Resend" : "Verify"}
                      </button>
                    )}
                  </div>
                  {form.email && !emailValid && <p className="text-xs text-destructive mt-1.5">Please enter a valid email address.</p>}
                  {emailVerified && <p className="text-xs text-secondary mt-1.5 flex items-center gap-1.5" data-testid="demo-email-verified"><ShieldCheck size={13} /> Email verified <button type="button" data-testid="demo-change-email" onClick={() => { setEmailVerified(false); setOtpSent(false); setOtp(""); setVerificationToken(""); }} className="text-muted-foreground underline hover:text-primary">Change</button></p>}

                  {otpSent && !emailVerified && (
                    <div className="mt-3 flex gap-2" data-testid="demo-otp-block">
                      <Input data-testid="demo-otp-input" inputMode="numeric" maxLength={6} placeholder="6-digit code" value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        className="rounded-xl bg-muted border-transparent h-12 tracking-[0.4em] font-semibold focus-visible:ring-2 focus-visible:ring-primary" />
                      <button type="button" data-testid="demo-verify-otp" onClick={verifyOtp} disabled={verifyingOtp || otp.length !== 6}
                        className="shrink-0 rounded-xl bg-secondary text-white px-5 h-12 text-sm font-semibold hover:-translate-y-0.5 transition-transform duration-200 disabled:opacity-50 disabled:translate-y-0 flex items-center gap-2">
                        {verifyingOtp ? <Loader2 className="animate-spin" size={16} /> : null}Confirm
                      </button>
                    </div>
                  )}
                  {otpSent && !emailVerified && <p className="text-xs text-muted-foreground mt-1.5">We emailed a code to {form.email}. It expires in 10 minutes.</p>}
                  {!emailVerified && <p className="text-xs text-primary/80 mt-2 flex items-start gap-1.5" data-testid="demo-verify-note"><Lock size={12} className="mt-0.5 shrink-0" /> Verify your email to unlock the rest of the form and book your demo.</p>}
                </div>

                <AnimatePresence>
                {emailVerified && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                    className="space-y-4 overflow-hidden"
                  >
                    <div>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-foreground/70 font-medium pointer-events-none select-none">+91</span>
                        <Input data-testid="demo-phone" aria-label="Mobile number, country code +91" inputMode="numeric" maxLength={10} placeholder="10-digit mobile number *" value={form.phone}
                          onChange={(e) => set("phone", e.target.value.replace(/\D/g, "").slice(0, 10))}
                          className={`rounded-xl bg-muted border-transparent h-12 pl-14 focus-visible:ring-2 focus-visible:ring-primary ${!phoneValid ? "ring-2 ring-destructive" : ""}`} />
                      </div>
                      {!phoneValid && <p className="text-xs text-destructive mt-1.5">Enter a valid 10-digit Indian mobile number (starting 6-9).</p>}
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="relative">
                        <select data-testid="demo-grade" value={form.grade} onChange={(e) => set("grade", e.target.value)}
                          className={`w-full appearance-none rounded-xl bg-muted h-12 px-4 pr-10 border border-transparent focus:outline-none focus:ring-2 focus:ring-primary ${form.grade ? "text-foreground" : "text-muted-foreground"}`}>
                          <option value="" disabled>Grade *</option>
                          {grades.map((g) => <option key={g} value={g}>{g}</option>)}
                        </select>
                        <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                      </div>
                      <div className="relative">
                        <select data-testid="demo-interest" value={form.plan_interest} onChange={(e) => set("plan_interest", e.target.value)}
                          className={`w-full appearance-none rounded-xl bg-muted h-12 px-4 pr-10 border border-transparent focus:outline-none focus:ring-2 focus:ring-primary ${form.plan_interest ? "text-foreground" : "text-muted-foreground"}`}>
                          <option value="" disabled>Interested in *</option>
                          {interests.map((g) => <option key={g} value={g}>{g}</option>)}
                        </select>
                        <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                      </div>
                    </div>

                    <Textarea data-testid="demo-message" placeholder="Anything we should know? (optional)" value={form.message} onChange={(e) => set("message", e.target.value)} className="rounded-xl bg-muted border-transparent focus-visible:ring-2 focus-visible:ring-primary min-h-[90px]" />

                    <button data-testid="demo-submit" type="submit" disabled={loading} className="w-full rounded-full bg-primary text-white py-4 font-bold flex items-center justify-center gap-2 hover:-translate-y-0.5 hover:shadow-lg transition-[transform,box-shadow] duration-300 disabled:opacity-60 disabled:translate-y-0">
                      {loading ? <><Loader2 className="animate-spin" size={18} /> Sending…</> : <><Send size={18} /> Request Free Demo</>}
                    </button>
                  </motion.div>
                )}
                </AnimatePresence>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
});

DemoForm.displayName = "DemoForm";
export default DemoForm;
